import { Plus, Trash2, Flame, Cable, Droplets } from 'lucide-react';
import { pathLength, pipeLatLngs, compressImage, uid } from '../lib/model';
import { feat } from '../lib/features';
import { P_LEVELS, DUCT_CFGS, CLARITY, lelOf, gasState, ductCount, tubeColor, tubeName, drumLen, hydQ, hydVol, hydSuggest, HYD_MIN_Q, HYD_MIN_PF, num, GAS_CLS, GAS_SURF } from '../lib/utility';

const Num = ({ testid, value, ph, onChange }) => <input inputMode="decimal" data-testid={testid} value={value ?? ''} placeholder={ph} onChange={(e) => onChange(e.target.value.replace(',', '.'))} />;
const f1 = (v, d = 1) => (v == null ? '–' : v.toFixed(d).replace('.', ','));

function GasFields({ t, u, set }) {
  const lel = lelOf(u.ppm), st = gasState(lel);
  return (
    <div className="sw-grid">
      <label>{t.utPl}<select data-testid="util-pl-select" value={u.pl || ''} onChange={(e) => set({ pl: e.target.value || null })}>
        <option value="">–</option>{P_LEVELS.map((k) => <option key={k} value={k}>{t['utPl_' + k]}</option>)}
      </select></label>
      <label>MOP (bar)<Num testid="util-mop-input" value={u.mop} ph="bar" onChange={(mop) => set({ mop })} /></label>
      <label>{t.utGas} (ppm)<Num testid="util-ppm-input" value={u.ppm} ph="ppm CH4" onChange={(ppm) => set({ ppm, gasT: Date.now() })} /></label>
      <label>% {t.utLel}<Num testid="util-lel-input" value={lel == null ? '' : +lel.toFixed(2)} ph="% UEG" onChange={(v) => set({ ppm: num(v) == null ? '' : String(Math.round(num(v) * 440)), gasT: Date.now() })} /></label>
      {st && <p className={`pr-alert ut-gas-${st}`} data-testid="util-gas-state">{st === 'ok' ? '✅' : '⚠️'} {f1(lel)} % {t.utLel} – {t['utGs_' + st]}</p>}
    </div>
  );
}

function Couplers({ t, u, set }) {
  const list = u.cpl || [];
  const upd = (i, p) => set({ cpl: list.map((c, k) => (k === i ? { ...c, ...p } : c)) });
  return (
    <div className="ut-list" data-testid="util-couplers">
      <small><b>{t.utCpl}</b> ({list.length})</small>
      {list.map((c, i) => (
        <div className="row" key={c.id}>
          <input className="ut-no" data-testid={`util-cpl-no-${i}`} value={c.no ?? ''} placeholder="Nr." onChange={(e) => upd(i, { no: e.target.value })} />
          <input data-testid={`util-cpl-maker-${i}`} value={c.maker || ''} placeholder={t.utMaker} onChange={(e) => upd(i, { maker: e.target.value })} />
          <Num testid={`util-cpl-sec-${i}`} value={c.sec} ph={t.utSec} onChange={(sec) => upd(i, { sec })} />
          <button className="icon-btn" data-testid={`util-cpl-del-${i}`} onClick={() => set({ cpl: list.filter((_, k) => k !== i) })}><Trash2 size={14} /></button>
        </div>
      ))}
      <button className="btn btn-outline btn-sm" data-testid="util-cpl-add" onClick={() => set({ cpl: [...list, { id: uid('cpl'), no: String(list.length + 1), maker: list[list.length - 1]?.maker || '', sec: '' }] })}><Plus size={14} /> {t.utCplAdd}</button>
    </div>
  );
}

function Duct({ t, u, set }) {
  const d = u.duct || {}, n = ductCount(d.cfg);
  const setD = (p) => set({ duct: { ...d, ...p } });
  return (
    <div className="ut-list" data-testid="util-duct">
      <label>{t.utDuct}<input list="ut-duct-cfgs" data-testid="util-duct-cfg" value={d.cfg || ''} placeholder="7x10/6" onChange={(e) => setD({ cfg: e.target.value })} /></label>
      <datalist id="ut-duct-cfgs">{DUCT_CFGS.map((c) => <option key={c} value={c} />)}</datalist>
      <div className="ut-tubes">
        {Array.from({ length: n }, (_, i) => (
          <label key={i} className="ut-tube" data-testid={`util-tube-${i}`}>
            <span className={`ut-dot ${tubeColor(i).stripe ? 'stripe' : ''}`} style={{ background: tubeColor(i).hex }} />
            <small>{tubeName(t, i)}</small>
            <input data-testid={`util-tube-use-${i}`} value={d.use?.[i] || ''} placeholder={t.utFree} onChange={(e) => setD({ use: { ...(d.use || {}), [i]: e.target.value } })} />
          </label>
        ))}
      </div>
    </div>
  );
}

function Drum({ t, u, set, planLen }) {
  const d = u.drum || {}, len = drumLen(d);
  const setD = (p) => set({ drum: { ...d, ...p } });
  return (
    <div className="sw-grid" data-testid="util-drum">
      <label>{t.utDrum} Nr.<input data-testid="util-drum-no" value={d.no || ''} onChange={(e) => setD({ no: e.target.value })} /></label>
      <label>{t.utCable}<input data-testid="util-drum-cable" value={d.cable || ''} placeholder="NAYY-J 4x150" onChange={(e) => setD({ cable: e.target.value })} /></label>
      <label>{t.utMarkStart} (m)<Num testid="util-drum-start" value={d.start} ph="m" onChange={(start) => setD({ start })} /></label>
      <label>{t.utMarkEnd} (m)<Num testid="util-drum-end" value={d.end} ph="m" onChange={(end) => setD({ end })} /></label>
      {len != null && <p className="sw-res" data-testid="util-drum-len">{t.utLaid}: <b>{f1(len)} m</b> · {t.utPlan} {f1(planLen)} m (Δ {f1(len - planLen)} m)</p>}
    </div>
  );
}

// gas, electrofusion, microduct and cable drum data of a pipe
export function PipeUtility({ t, obj, patch, fittings }) {
  const bld = feat('util'), gas = obj.type === 'gas' && feat('gas'), cable = bld && (obj.type === 'power' || obj.type === 'telecom'), pe = bld && (obj.type === 'gas' || obj.type === 'water');
  if (!gas && !pe && !cable) return null;
  const u = obj.util || {};
  const set = (p) => patch({ util: { ...u, ...p } });
  return (
    <div className="fx-box ut-box" data-testid="pipe-utility-box">
      <b>{obj.type === 'gas' ? <Flame size={14} /> : cable ? <Cable size={14} /> : <Droplets size={14} />} {obj.type === 'gas' ? t.utTitleGas : cable ? t.utTitleCable : t.utCpl}</b>
      {gas && <GasFields t={t} u={u} set={set} />}
      {pe && <Couplers t={t} u={u} set={set} />}
      {obj.type === 'telecom' && <Duct t={t} u={u} set={set} />}
      {cable && <Drum t={t} u={u} set={set} planLen={pathLength(pipeLatLngs(obj, fittings))} />}
    </div>
  );
}

function HydEntry({ t, e, upd, del, i }) {
  const sug = hydSuggest(e), q = hydQ(e), v = hydVol(e);
  return (
    <div className={`ut-hyd ut-res-${e.res || 'none'}`} data-testid={`hyd-entry-${i}`}>
      <div className="row"><small><b>{new Date(e.t).toLocaleString()}</b></small><button className="icon-btn" data-testid={`hyd-del-${i}`} onClick={del}><Trash2 size={14} /></button></div>
      <div className="sw-grid">
        <label>{t.hyPs} (bar)<Num testid={`hyd-ps-${i}`} value={e.ps} ph="bar" onChange={(ps) => upd({ ps })} /></label>
        <label>{t.hyPf} (bar)<Num testid={`hyd-pf-${i}`} value={e.pf} ph="bar" onChange={(pf) => upd({ pf })} /></label>
        <label>{t.hyQ}<span className="row"><Num testid={`hyd-q-${i}`} value={e.q} ph={e.unit === 'lmin' ? 'l/min' : 'm³/h'} onChange={(qq) => upd({ q: qq })} />
          <select data-testid={`hyd-unit-${i}`} value={e.unit || 'm3h'} onChange={(x) => upd({ unit: x.target.value })}><option value="m3h">m³/h</option><option value="lmin">l/min</option></select></span></label>
        <label>{t.hyDur} (min)<Num testid={`hyd-dur-${i}`} value={e.dur} ph="min" onChange={(dur) => upd({ dur })} /></label>
      </div>
      <div className="chip-group" data-testid={`hyd-clarity-${i}`}>
        {CLARITY.map((c) => <button key={c} className={`chip seg ut-clar-${c} ${e.clar === c ? 'on' : ''}`} data-testid={`hyd-clar-${c}-${i}`} onClick={() => upd({ clar: c })}>{t['hyC_' + c]}</button>)}
      </div>
      <p className="sw-res" data-testid={`hyd-calc-${i}`}>Q {f1(q)} m³/h ({f1(q == null ? null : q / 0.06, 0)} l/min) · Δp {f1(num(e.ps) != null && num(e.pf) != null ? num(e.ps) - num(e.pf) : null, 2)} bar · {t.hyVol} {f1(v, 2)} m³</p>
      {sug && <small className="muted" data-testid={`hyd-suggest-${i}`}>{t.hySuggest}: <b>{t['hyR_' + sug]}</b> ({t.hyNorm.replace('{q}', HYD_MIN_Q).replace('{p}', String(HYD_MIN_PF).replace('.', ','))})</small>}
      <div className="row">
        <label className="btn btn-outline btn-sm">📷 {t.dsPhoto}<input hidden type="file" accept="image/*" capture="environment" data-testid={`hyd-photo-${i}`} onChange={async (x) => { const fl = x.target.files?.[0]; if (fl) upd({ photo: (await compressImage(fl, 1200, 0.75)).data }); }} /></label>
        {e.photo && <img src={e.photo} alt="" className="ds-ph" data-testid={`hyd-photo-thumb-${i}`} />}
        <button className={`chip seg ${e.res === 'pass' ? 'on ut-pass' : ''}`} data-testid={`hyd-pass-${i}`} onClick={() => upd({ res: 'pass' })}>✅ {t.hyR_pass}</button>
        <button className={`chip seg ${e.res === 'fail' ? 'on ut-fail' : ''}`} data-testid={`hyd-fail-${i}`} onClick={() => upd({ res: 'fail' })}>❌ {t.hyR_fail}</button>
      </div>
    </div>
  );
}

// hydrant flow & flushing protocol (several measurements per hydrant)
export function HydrantLog({ t, obj, patch }) {
  if (obj.type !== 'hydrant') return null;
  const logs = obj.hydLogs || [];
  const upd = (i, p) => patch({ hydLogs: logs.map((e, k) => (k === i ? { ...e, ...p } : e)) });
  return (
    <div className="fx-box ut-box" data-testid="hydrant-log-box">
      <b><Droplets size={14} /> {t.hyTitle} ({logs.length})</b>
      {logs.map((e, i) => <HydEntry key={e.id} t={t} e={e} i={i} upd={(p) => upd(i, p)} del={() => window.confirm(t.hyDelQ) && patch({ hydLogs: logs.filter((_, k) => k !== i) })} />)}
      <button className="btn btn-dark btn-sm" data-testid="hyd-add-btn" onClick={() => patch({ hydLogs: [{ id: uid('hyd'), t: Date.now(), unit: 'm3h', ps: logs[0]?.ps ?? '' }, ...logs] })}><Plus size={14} /> {t.hyAdd}</button>
    </div>
  );
}

// gas leak / bar hole: concentration (ppm, % LEL, vol.-% CH4), hazard class A/B/C, surface conditions
export function GasLeak({ t, obj, patch }) {
  const g = obj.gl || {};
  const ppm = num(g.ppm), lel = lelOf(g.ppm), vol = ppm == null ? null : ppm / 10000;
  const set = (p) => patch({ gl: { ...g, ...p, t: Date.now() }, ...(p.cls ? { severity: { A: 'large', B: 'medium', C: 'small' }[p.cls] } : {}) });
  const fromPpm = (v) => set({ ppm: v });
  return (
    <div className={`fx-box ut-box gl-box gl-${g.cls || 'none'}`} data-testid="gas-leak-box">
      <b><Flame size={14} /> {t.glTitle}</b>
      <div className="sw-grid">
        <label>ppm<Num testid="gas-leak-ppm" value={g.ppm} ph="ppm" onChange={fromPpm} /></label>
        <label>% {t.utLel}<Num testid="gas-leak-lel" value={lel == null ? '' : +lel.toFixed(2)} ph="% UEG" onChange={(v) => fromPpm(num(v) == null ? '' : String(Math.round(num(v) * 440)))} /></label>
        <label>Vol.-% CH4<Num testid="gas-leak-vol" value={vol == null ? '' : +vol.toFixed(3)} ph="Vol.-%" onChange={(v) => fromPpm(num(v) == null ? '' : String(Math.round(num(v) * 10000)))} /></label>
      </div>
      <div className="gl-cls" data-testid="gas-leak-class">
        {GAS_CLS.map((c) => <button key={c} className={`gl-cbtn gl-c${c} ${g.cls === c ? 'on' : ''}`} data-testid={`gas-leak-class-${c}`} onClick={() => set({ cls: c })}>{t['glC_' + c]}</button>)}
      </div>
      <div className="gl-surf">
        {GAS_SURF.map((s) => <label key={s} className="fx-check"><input type="checkbox" data-testid={`gas-leak-surf-${s}`} checked={!!g.surf?.[s]} onChange={(e) => set({ surf: { ...(g.surf || {}), [s]: e.target.checked } })} /> {t['glS_' + s]}</label>)}
      </div>
    </div>
  );
}
